import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { usePlanetStore } from '../store/planetStore';
import { usePlayerStore } from '../store/playerStore';
import { useConfirmDialog } from '../contexts/ConfirmDialogContext';
import { usePlanetResource } from '../hooks/usePlanetResource';
import { useShellChrome } from '../hooks/useShellChrome';
import { useSurfaceFits } from '../hooks/useSurfaceFits';
import EditTextModal from '../components/EditTextModal';
import PlanetImagePickerModal from '../components/PlanetImagePickerModal';
import ScanForSpiesButton from '../components/ScanForSpiesButton';
import TeamPlanetConversionCard from '../components/TeamPlanetConversionCard';
import PlanetSurface from '../components/planet/PlanetSurface';
import { PlanetZoneLinksFallback } from '../components/planet/PlanetZoneLinksFallback';
import { Panel } from '../components/ui/Panel';
import { Button } from '../components/ui/Button';
import { Skeleton } from '../components/ui/Skeleton';
import { Spinner } from '../components/ui/Spinner';
import { countPendingBuildsByZone } from '../utils/buildQueue';
import { updatePlanet, deletePlanet } from '../services/planet.service';
import { calculateOzoneChange, calculateUsedPopulation, isPlanetOzoneLocked } from '@kaeliux/game-engine';
import { OZONE_MAX, OZONE_CRITICAL_LEVEL, RESOURCE_NAMES, type PlayerRace, type TickCheckpointEventOf } from '@kaeliux/shared';

/**
 * Reserved chrome, other than `AppShell`'s HUD/dock, that the surface stage
 * competes with on `/planet`: page padding plus the header/status column
 * beside it on desktop collapsing above/below it on mobile. Feeds
 * `useSurfaceFits` (DS-18 scope item 4) alongside the shell's measured
 * HUD/dock heights.
 */
const PAGE_CHROME_RESERVE_PX = 96;

function formatNumber(value: number) {
    return Math.floor(value).toLocaleString();
}

/** Where the critical-level marker sits on the Ozone bar. Both ends are constants. */
const OZONE_CRITICAL_PERCENT = (OZONE_CRITICAL_LEVEL / OZONE_MAX) * 100;

function NoPlanetFallback({ userId, race }: { userId: string; race?: PlayerRace }) {
    const { createInitialPlanet } = usePlanetStore();
    const { refreshProfile } = useAuthStore();
    const navigate = useNavigate();
    const [isCreating, setIsCreating] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const attempted = useRef(false);

    const create = () => {
        if (!race || isCreating) return;
        setIsCreating(true);
        setError(null);
        createInitialPlanet(userId, race)
            .then(() => {
                // Planet is already loaded in the store; navigate directly so the
                // page doesn't have to wait for refreshProfile to update the store.
                refreshProfile().catch(console.error);
                navigate('/planet', { replace: true });
            })
            .catch((err: unknown) => {
                setError(err instanceof Error ? err.message : 'Failed to create planet. Please try again.');
            })
            .finally(() => setIsCreating(false));
    };

    useEffect(() => {
        if (race && !attempted.current) {
            attempted.current = true;
            create();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!race) {
        return (
            <main className="max-w-7xl mx-auto p-4">
                <Panel>
                    <p className="text-content-muted">
                        We could not locate your current planet. Please contact support if this issue persists.
                    </p>
                </Panel>
            </main>
        );
    }

    return (
        <main className="max-w-7xl mx-auto p-4">
            <Panel className="flex flex-col items-start gap-3">
                {isCreating && (
                    <div className="flex items-center gap-3 text-content-muted">
                        <Spinner size="md" decorative={false} aria-label="Initializing homeworld" className="shrink-0" />
                        <span>Initializing homeworld…</span>
                    </div>
                )}
                {error && (
                    <>
                        <p className="text-status-danger text-body">{error}</p>
                        <Button variant="primary" onClick={create}>
                            Retry
                        </Button>
                    </>
                )}
            </Panel>
        </main>
    );
}

function PlanetPageContent() {
    const { state: { planet, isLoading }, requestTick } = usePlanetResource();
    const { profile } = useAuthStore();
    const { points, sciences } = usePlayerStore();
    const { loadPlanets, setActivePlanet } = usePlanetStore();
    const { openDialog } = useConfirmDialog();
    const navigate = useNavigate();
    const zoneQueueCounts = useMemo(() => countPendingBuildsByZone(planet), [planet]);
    const [editNameOpen, setEditNameOpen] = useState(false);
    const [editImageOpen, setEditImageOpen] = useState(false);
    const [editError, setEditError] = useState<string | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);

    async function handleRenamePlanet(name: string) {
        if (!planet) return;
        setEditError(null);
        try {
            await updatePlanet(planet.id, { name });
            await requestTick();
            // The renamed planet also appears in the top nav's planet
            // switcher dropdown, which reads from the planet list rather
            // than `currentPlanet` — refresh it so the new name shows
            // immediately instead of waiting up to 60s for the periodic sync.
            if (profile?.id) loadPlanets(profile.id).catch(() => { });
            setEditNameOpen(false);
        } catch (err: unknown) {
            setEditError(err instanceof Error ? err.message : 'Failed to rename planet.');
        }
    }

    async function handleChangePlanetImage(planetType: number) {
        if (!planet) return;
        setEditError(null);
        try {
            await updatePlanet(planet.id, { planetType });
            await requestTick();
            if (profile?.id) loadPlanets(profile.id).catch(() => { });
            setEditImageOpen(false);
        } catch (err: unknown) {
            setEditError(err instanceof Error ? err.message : 'Failed to change planet image.');
        }
    }

    async function handleDeletePlanet() {
        if (!planet || !profile?.id || isDeleting) return;
        setEditError(null);
        setIsDeleting(true);
        try {
            await deletePlanet(planet.id);
            await loadPlanets(profile.id);
            if (profile.mainPlanetId) {
                await setActivePlanet(profile.mainPlanetId, profile.id);
            }
            navigate('/planet', { replace: true });
        } catch (err: unknown) {
            setEditError(err instanceof Error ? err.message : 'Failed to delete planet.');
        } finally {
            setIsDeleting(false);
        }
    }

    function requestDeletePlanet() {
        if (!planet) return;
        openDialog({
            title: `Delete ${planet.name}?`,
            message: 'This will permanently delete this planet and all its buildings and ships. This cannot be undone.',
            confirmLabel: 'Delete planet',
            danger: true,
            onConfirm: () => { handleDeletePlanet().catch(() => { }); },
        });
    }

    // Server-computed points (`commitAdvance` in apps/functions/src/advance.ts
    // recalculates and stores these on every mutating request) — read
    // straight from the store, never recomputed client-side.
    const resourcePoints = points?.resource ?? 0;
    const battlePoints = points?.battle ?? 0;
    const totalPoints = points?.total ?? 0;

    const shipQueue = useMemo(
        () => (planet?.tickCheckpoints ?? []).filter(
            (e): e is TickCheckpointEventOf<'shipBuild'> => e.eventType === 'shipBuild' && !e.cancelled
        ),
        [planet]
    );
    const shipQueueCount = shipQueue.length;
    const usedPopulation = planet && profile?.race
        ? calculateUsedPopulation(profile.race, planet.ships, planet.tickCheckpoints, planet.shipsInFlight)
        : 0;

    const { hudHeight, navHeight } = useShellChrome();
    const surfaceFits = useSurfaceFits(hudHeight + navHeight + PAGE_CHROME_RESERVE_PX);
    const stageHeight = `calc(100vh - ${hudHeight + navHeight + PAGE_CHROME_RESERVE_PX}px)`;

    if (isLoading || !planet) {
        return (
            <main className="max-w-7xl mx-auto p-4 flex flex-col gap-4 lg:flex-row lg:items-start lg:gap-6" aria-busy="true">
                <Skeleton variant="block" className="h-[420px] lg:flex-1" data-testid="planet-page-skeleton-surface" />
                <div className="flex flex-col gap-4 lg:w-64">
                    <Skeleton variant="block" className="h-40" />
                    <Skeleton variant="block" className="h-32" />
                </div>
            </main>
        );
    }

    const planetImage = new URL(`../assets/images/planets/planet_${planet.planetType}.png`, import.meta.url).href;
    const ozoneRatio = (planet.state.ozone.current / OZONE_MAX) * 100;
    const populationRatio = planet.resources.population.capacity > 0
        ? (usedPopulation / planet.resources.population.capacity) * 100
        : 0;
    const ozoneLocked = isPlanetOzoneLocked(planet);
    const ozoneChangePerDay = calculateOzoneChange(planet.buildings, sciences?.ecology ?? 0, 86_400);

    return (
        <div className="bg-transparent flex flex-col">

            <main className="flex-1 w-full max-w-7xl mx-auto px-4 py-4 flex flex-col gap-4">
                {/* The surface is the primary hub (DS-18) — it gets the
                    full page width so its stage has room to lay out and
                    tap every one of the twelve building slots, rather than
                    sharing a row with the identity/status panels below. */}
                {surfaceFits ? (
                    <div
                        className="relative w-full overflow-hidden rounded-panel border border-space-700"
                        style={{ height: stageHeight }}
                        data-testid="planet-page-surface"
                    >
                        {/* No `dockHeight`: this stage is already inset above
                            the dock by `stageHeight`, so reserving the dock's
                            height again inside it would push the zone strip up
                            into the building labels. */}
                        <PlanetSurface planet={planet} race={profile?.race} />
                    </div>
                ) : (
                    <PlanetZoneLinksFallback zoneQueueCounts={zoneQueueCounts} shipQueueCount={shipQueueCount} />
                )}

            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:gap-6">
                <Panel variant="solid" padding="md" className="flex flex-col items-center lg:flex-1">
                    <div className="flex flex-row items-center gap-4 w-full">
                        <img
                            src={planetImage}
                            alt={planet.name}
                            data-testid="planet-header-image"
                            className="w-24 h-24 rounded-full object-cover border border-space-700 shrink-0"
                        />
                        <div>
                            <h2 className="text-display">{planet.name}</h2>
                            <p className="text-content-muted text-body">G:{planet.coordinates.galaxy} S:{planet.coordinates.system} P:{planet.coordinates.position}</p>
                        </div>
                    </div>
                    <div className="mt-3 flex gap-2 self-start">
                        <Button
                            onClick={() => setEditImageOpen(true)}
                            data-tutorial-id="planet-edit-photo"
                            variant="secondary"
                            size="sm"
                        >
                            Change Photo
                        </Button>
                        <Button
                            onClick={() => setEditNameOpen(true)}
                            data-tutorial-id="planet-edit-name"
                            variant="primary"
                            size="sm"
                        >
                            Edit Name
                        </Button>
                        {profile?.mainPlanetId && planet.id !== profile.mainPlanetId && (
                            <Button
                                onClick={requestDeletePlanet}
                                disabled={isDeleting}
                                data-tutorial-id="planet-delete"
                                variant="danger"
                                size="sm"
                            >
                                {isDeleting ? 'Deleting…' : 'Delete Planet'}
                            </Button>
                        )}
                        <ScanForSpiesButton
                            planetId={planet.id}
                            intelligenceLevel={sciences?.intelligence ?? 0}
                            lastSpyScanAt={planet.lastSpyScanAt}
                        />
                    </div>
                    {editError && <p className="text-status-danger text-body mt-2 self-start">{editError}</p>}
                </Panel>

                <EditTextModal
                    open={editNameOpen}
                    title="Edit planet name"
                    label="Planet name"
                    initialValue={planet.name}
                    onSubmit={(value) => { void handleRenamePlanet(value); }}
                    onCancel={() => setEditNameOpen(false)}
                />

                <PlanetImagePickerModal
                    open={editImageOpen}
                    currentPlanetType={planet.planetType}
                    onSelect={(planetType) => { void handleChangePlanetImage(planetType); }}
                    onCancel={() => setEditImageOpen(false)}
                />

                <div className="flex flex-col gap-4 lg:w-64">
                    <Panel variant="solid" padding="md">
                        <h2 className="text-title text-content-primary mb-3">Planet Status</h2>
                        <div className="space-y-3 text-body text-content-secondary" data-tutorial-id="ozone-resource">
                            <div className="flex justify-between">
                                <span
                                    className="cursor-help underline decoration-dotted decoration-content-muted"
                                    title={`Below the critical level (${formatNumber(OZONE_CRITICAL_LEVEL)}), the planet locks: no building/ship-upgrade queues, no fleet dispatch, no research. Restore ${RESOURCE_NAMES.ozone} (Ecology science, or a premium purchase) to unlock.`}
                                >
                                    {RESOURCE_NAMES.ozone}
                                </span>
                                <span>{formatNumber(planet.state.ozone.current)} / {formatNumber(OZONE_MAX)}</span>
                            </div>
                            <div className="relative w-full bg-space-900 rounded-full h-3 overflow-hidden">
                                <div className="h-full bg-status-info" style={{ width: `${Math.min(100, Math.max(0, ozoneRatio))}%` }} />
                                <div
                                    className="absolute inset-y-0 w-px bg-status-danger/80"
                                    style={{ left: `${OZONE_CRITICAL_PERCENT}%` }}
                                    title={`Critical level: ${formatNumber(OZONE_CRITICAL_LEVEL)}`}
                                />
                            </div>
                            {ozoneLocked ? (
                                <div className="text-status-danger text-body font-semibold">{`Planet locked: ${RESOURCE_NAMES.ozone} supply critical`}</div>
                            ) : (
                                <div className="flex items-center justify-between text-body">
                                    <span className="text-content-muted">{`${RESOURCE_NAMES.ozone} supply stable`}</span>
                                    <span className={ozoneChangePerDay >= 0 ? 'text-status-success' : 'text-status-danger'}>
                                        {ozoneChangePerDay >= 0 ? '+' : ''}{formatNumber(ozoneChangePerDay)}/day
                                    </span>
                                </div>
                            )}
                            <div className="flex justify-between">
                                <span>Population</span>
                                <span data-testid="planet-population-value">{formatNumber(usedPopulation)} / {formatNumber(planet.resources.population.capacity)}</span>
                            </div>
                            <div className="w-full bg-space-900 rounded-full h-3 overflow-hidden">
                                <div className="h-full bg-status-success" style={{ width: `${Math.min(100, Math.max(0, populationRatio))}%` }} />
                            </div>
                        </div>
                    </Panel>

                    <Panel variant="solid" padding="md" data-tutorial-id="player-points">
                        <h2 className="text-title text-content-primary mb-3">Points</h2>
                        <div className="space-y-3 text-body text-content-secondary">
                            <div className="flex justify-between">
                                <span>Total Points</span>
                                <span className="text-content-primary font-semibold">{formatNumber(totalPoints)}</span>
                            </div>
                            <div className="flex justify-between">
                                <span>Resource Points</span>
                                <span>{formatNumber(resourcePoints)}</span>
                            </div>
                            <div className="flex justify-between">
                                <span>Battle Points</span>
                                <span>{formatNumber(battlePoints)}</span>
                            </div>
                        </div>
                    </Panel>

                    <TeamPlanetConversionCard planet={planet} />
                </div>
            </div>
            </main>
        </div>
    );
}

// The `/planet/:planetId` route is kept as an alias for old links, but the id
// in it is deliberately ignored: which planet the app manages is the switcher's
// choice (`activePlanetId`), and AppShell drives every load from that. Reading
// the param here as well would let a hand-typed url fight the shell over
// `currentPlanet` indefinitely.
export default function PlanetPage() {
    const { profile } = useAuthStore();
    const activePlanetId = usePlanetStore((s) => s.activePlanetId);

    if (!profile) {
        return (
            <main className="max-w-7xl mx-auto p-4" aria-busy="true">
                <Skeleton variant="block" className="h-[420px]" data-testid="planet-page-skeleton-profile" />
            </main>
        );
    }

    const planetId = activePlanetId ?? profile.mainPlanetId ?? '';

    if (!planetId) {
        return (
            <NoPlanetFallback userId={profile.id} race={profile.race} />
        );
    }

    return (
        <PlanetPageContent />
    );
}
